import { useEffect, useState } from "react";
import { ArrowLeft, Check, Download, ExternalLink, KeyRound, Save, AlarmClock, Trash2 } from "lucide-react";
import { FEEDBACK_LABELS, FEEDBACK_STATES, type FeedbackOriginal, type FeedbackState } from "../../core/beta-feedback";
import { AdminSection } from "./AdminSection";
import { RefreshButton } from "./RefreshButton";
import "../feedback.css";

interface FeedbackSummary {
	id: string;
	revision: number;
	kind: string;
	comment: string | null;
	state: FeedbackState;
	created_at: string | null;
	build: string | null;
	device: string | null;
	images: number;
}
interface FeedbackDetail {
	id: string;
	state: FeedbackState;
	revision: number;
	original: Omit<FeedbackOriginal, "screenshots" | "resource">;
	attachments: Array<{ ordinal: number; content_type: string; bytes: number; url: string }>;
	history: Array<{ revision: number; state: string; reviewer: string; reviewed_at: number; findings: string; refs: string }>;
	notification_state: string;
	notification_error: string | null;
}
interface Inbox {
	items: FeedbackSummary[];
	inboxCount: number;
	nextOffset: number | null;
	configured: boolean;
	sync: { last_success_at: number | null; last_error: string | null; backfill_done: number };
}
interface MachineToken { id: string; label: string; scope: string; expires_at: number; revoked_at: number | null }

async function api<T>(path = "", init?: RequestInit): Promise<T> {
	const response = await fetch(`/api/admin/feedback${path}`, init);
	const data = await response.json();
	if (!response.ok) throw new Error(data.error?.message ?? `Request failed (${response.status}).`);
	return data as T;
}
const post = (value: unknown): RequestInit => ({ method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(value) });
const when = (date: string | number | null) => date == null ? "Unknown" : new Date(date).toLocaleString();

function download(value: unknown, filename: string) {
	const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }));
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	anchor.click();
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function FeedbackPanel() {
	const [filter, setFilter] = useState("open");
	const [offset, setOffset] = useState(0);
	const [selected, setSelected] = useState(() => new URLSearchParams(location.search).get("feedback"));
	const [inbox, setInbox] = useState<Inbox | null>(null);
	const [detail, setDetail] = useState<FeedbackDetail | null>(null);
	const [revision, setRevision] = useState(0);
	const [state, setState] = useState<FeedbackState>("open");
	const [findings, setFindings] = useState("");
	const [references, setReferences] = useState("");
	const [status, setStatus] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [loading, setLoading] = useState(true);
	const [tokens, setTokens] = useState<MachineToken[] | null>(null);
	const [label, setLabel] = useState("");
	const [scope, setScope] = useState("read");
	const [showTokens, setShowTokens] = useState(false);

	useEffect(() => {
		const controller = new AbortController();
		setInbox(null);
		setLoading(true);
		void api<Inbox>(`?state=${filter}&offset=${offset}`, { signal: controller.signal }).then(setInbox).catch(cause => {
			if (!controller.signal.aborted) setStatus(cause.message);
		}).finally(() => { if (!controller.signal.aborted) setLoading(false); });
		return () => controller.abort();
	}, [filter, offset, revision]);

	useEffect(() => {
		const controller = new AbortController();
		setDetail(null);
		if (selected) void api<FeedbackDetail>(`/${encodeURIComponent(selected)}`, { signal: controller.signal }).then(value => {
			setDetail(value); setState(value.state); setFindings(value.history[0]?.findings ?? ""); setReferences(value.history[0]?.refs ?? "");
		}).catch(cause => { if (!controller.signal.aborted) setStatus(cause.message); });
		return () => controller.abort();
	}, [selected, revision]);

	useEffect(() => {
		const update = () => setSelected(new URLSearchParams(location.search).get("feedback"));
		window.addEventListener("popstate", update);
		return () => window.removeEventListener("popstate", update);
	}, []);

	const choose = (id: string | null) => {
		const url = new URL(location.href);
		if (id) url.searchParams.set("feedback", id); else url.searchParams.delete("feedback");
		history.pushState(null, "", url);
		setSelected(id); setStatus(null);
	};
	const act = async (action: () => Promise<void>) => {
		setBusy(true); setStatus(null);
		try { await action(); } catch (cause) { setStatus(cause instanceof Error ? cause.message : "Feedback request failed."); }
		finally { setBusy(false); }
	};
	const loadTokens = async () => setTokens((await api<{ tokens: MachineToken[] }>("/tokens")).tokens);
	const save = async (item: { id: string; revision: number }, disposition: FeedbackState, notes = "", refs = "") => {
		await api(`/${item.id}/review`, post({ state: disposition, findings: notes, references: refs, expectedRevision: item.revision }));
		if (selected === item.id) choose(null);
		setOffset(0);
		setRevision(value => value + 1);
		setStatus(`${FEEDBACK_LABELS[disposition]} saved.`);
	};

	return <AdminSection title={<>Feedback <span className="admin-disclosure__count">{inbox ? `${inbox.inboxCount} in inbox` : "…"}</span></>} className="feedback" id="feedback" defaultOpen={false} revealKey={selected}>
		<div className="admin-section__head">
			<div className="admin-filters">
				<div className="feedback-views" role="group" aria-label="Feedback view">
					<button type="button" aria-pressed={filter === "open"} onClick={() => { setFilter("open"); setOffset(0); choose(null); }}>Inbox</button>
					<button type="button" aria-pressed={filter !== "open"} onClick={() => { setFilter("archive"); setOffset(0); choose(null); }}>Archive</button>
				</div>
				{filter !== "open" && <label>Filter disposition<select value={filter} onChange={event => { setFilter(event.target.value); setOffset(0); }}>
					<option value="archive">All archived</option>{FEEDBACK_STATES.filter(value => value !== "open").map(value => <option key={value} value={value}>{FEEDBACK_LABELS[value]}</option>)}
				</select></label>}
				<RefreshButton className="admin-button" label="Import new feedback" text="Import" size={16} busy={busy} disabled={inbox?.configured === false} onClick={() => void act(async () => {
					await api("/refresh", post({})); setStatus("Import queued. Reload after the next sync.");
				})} />
				<RefreshButton className="admin-button" label="Reload inbox" disabled={busy} busy={loading} onClick={() => setRevision(value => value + 1)} size={16} />
				<button className="admin-button" title="Agent credentials" aria-label="Agent credentials" aria-expanded={showTokens} onClick={() => void act(async () => { setShowTokens(!showTokens); await loadTokens(); })}><KeyRound size={16} /></button>
			</div>
		</div>
		{status && <p className="admin-status" role="status">{status}</p>}
		{inbox && <p className="feedback-sync">{inbox.configured ? `Last sync: ${when(inbox.sync.last_success_at)}${inbox.sync.backfill_done ? "" : " / Historical import pending"}` : "Apple credentials not configured"}{inbox.sync.last_error && ` / ${inbox.sync.last_error}`}</p>}
		{showTokens && <div className="feedback-credentials">
			<form className="admin-filters" onSubmit={event => { event.preventDefault(); void act(async () => {
				const credential = await api<{ token: string; id: string; expiresAt: number }>("/tokens", post({ label, scope }));
				download({ origin: location.origin, ...credential }, "gotime-feedback-credential.json");
				setLabel(""); await loadTokens();
			}); }}>
				<label>Agent name<input required maxLength={60} pattern="[A-Za-z0-9 ._\-]+" value={label} onChange={event => setLabel(event.target.value)} /></label>
				<label>Access<select value={scope} onChange={event => setScope(event.target.value)}><option value="read">Read only</option><option value="review">Read and review</option></select></label>
				<button className="admin-button" disabled={busy || !label.trim()}><Download size={16} /> Create credential</button>
			</form>
			{tokens?.map(token => <div className="feedback-token" key={token.id}><span>{token.label} / {token.scope} / {token.revoked_at ? "Revoked" : `Expires ${when(token.expires_at)}`}</span>
				<button className="admin-button" aria-label={`Revoke ${token.label}`} title={`Revoke ${token.label}`} disabled={busy || !!token.revoked_at} onClick={() => void act(async () => { await api(`/tokens/${token.id}`, { method: "DELETE" }); await loadTokens(); })}><Trash2 size={16} /></button>
			</div>)}
		</div>}
		<div className={`feedback-layout${selected ? " feedback-layout--detail" : ""}`}>
			<div className="feedback-list" aria-label="Feedback submissions">
				{!inbox && <p role="status">Loading feedback...</p>}
				{inbox?.items.length === 0 && <p>{filter === "open" ? "Inbox clear." : filter === "archive" ? "No archived feedback." : `No ${filter} feedback.`}</p>}
				{inbox?.items.map(item => <article className={`feedback-item${selected === item.id ? " feedback-item--selected" : ""}`} key={item.id}><a href={`/admin?feedback=${item.id}`} onClick={event => {
					if (!event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); choose(item.id); }
				}}><span className="feedback-item__meta">{FEEDBACK_LABELS[item.state]} / {item.kind} / {item.images} images</span><strong>{item.comment || "No comment supplied"}</strong><span className="feedback-item__meta">{when(item.created_at)} / Build {item.build ?? "unknown"} / {item.device ?? "Unknown device"}</span></a>
					<div className="admin-filters">
						<button className="admin-button" title="Mark fixed" aria-label="Mark fixed" disabled={busy || item.state === "fixed"} onClick={() => void act(() => save(item, "fixed"))}><Check size={16} /></button>
						<button className="admin-button" title="Snooze in archive" aria-label="Snooze in archive" disabled={busy || item.state === "snoozed"} onClick={() => void act(() => save(item, "snoozed"))}><AlarmClock size={16} /></button>
						<select aria-label={`Disposition for ${item.comment || item.id}`} value={item.state} disabled={busy} onChange={event => void act(() => save(item, event.target.value as FeedbackState))}>
							{FEEDBACK_STATES.map(value => <option key={value} value={value}>{FEEDBACK_LABELS[value]}</option>)}
						</select>
					</div>
				</article>)}
				<div className="admin-filters">
					{offset > 0 && <button className="admin-button" onClick={() => setOffset(Math.max(0, offset - 50))}>Previous</button>}
					{inbox?.nextOffset != null && <button className="admin-button" onClick={() => setOffset(inbox.nextOffset!)}>Next</button>}
				</div>
			</div>
			{selected && <div className="feedback-detail">
				<div className="admin-filters"><button className="admin-button" onClick={() => choose(null)}><ArrowLeft size={16} /> {filter === "open" ? "Inbox" : "Archive"}</button>
					{detail && <button className="admin-button" onClick={() => download(detail, `feedback-${detail.id}.json`)}><Download size={16} /> Export</button>}
					<a className="admin-button" title="Permanent link" aria-label="Permanent link" href={`/admin?feedback=${encodeURIComponent(selected)}`}><ExternalLink size={16} /></a>
				</div>
				{!detail && <p role="status">Loading submission...</p>}
				{detail && <>
					<h3>{detail.original.kind === "crash" ? "Crash feedback" : "Screenshot feedback"}</h3>
					<p className="feedback-comment">{detail.original.comment || "No comment supplied"}</p>
					<p className="feedback-sync">{when(detail.original.createdAt)} / Notification: {detail.notification_state}</p>
					<div className="admin-filters">
						<button className="admin-button" disabled={busy} onClick={() => void act(() => save(detail, "fixed", findings, references))}><Check size={16} /> Mark fixed</button>
						<button className="admin-button" disabled={busy} onClick={() => void act(() => save(detail, "snoozed", findings, references))}><AlarmClock size={16} /> Snooze</button>
					</div>
					<div className="feedback-images">{detail.attachments.map(image => <a key={image.ordinal} href={image.url} target="_blank" rel="noreferrer"><img src={image.url} loading="lazy" alt={`Submitted screenshot ${image.ordinal + 1}`} /></a>)}</div>
					<details><summary>Original metadata</summary><pre>{JSON.stringify({ metadata: detail.original.metadata, build: detail.original.build, tester: detail.original.tester }, null, 2)}</pre></details>
					<form className="feedback-review" onSubmit={event => { event.preventDefault(); void act(async () => {
						await save(detail, state, findings, references);
					}); }}>
						<label>Disposition<select value={state} onChange={event => setState(event.target.value as FeedbackState)}>{FEEDBACK_STATES.map(value => <option key={value} value={value}>{value === "open" && detail.state !== "open" ? "Open (reopen)" : FEEDBACK_LABELS[value]}</option>)}</select></label>
						<label>Notes (optional)<textarea rows={5} maxLength={20000} value={findings} onChange={event => setFindings(event.target.value)} /></label>
						<label>Issue, commit and release references<textarea rows={2} maxLength={4000} value={references} onChange={event => setReferences(event.target.value)} /></label>
						<button className="admin-button" disabled={busy}><Save size={16} /> Save</button>
					</form>
					<h3>Review history</h3>
					{detail.history.length === 0 && <p>No reviews yet.</p>}
					{detail.history.map(entry => <article className="feedback-history" key={entry.revision}><strong>{entry.state} / {entry.reviewer}</strong><p>{when(entry.reviewed_at)}</p><p className="feedback-comment">{entry.findings}</p><p className="feedback-comment">{entry.refs}</p></article>)}
				</>}
			</div>}
		</div>
	</AdminSection>;
}